import React, { useContext, useRef, useState } from 'react';
import classnames from 'clsx';
import styles from '../button.scss';
import { TestIds } from '../../../../constants';
import StylesContext from '../../../StylesContext';
import type { TextSizeBlockType } from './utils/utils';
import {
  textSizeBlockTypes,
  getActiveTextSize,
  getStateWithNewTextSize,
  preventBubblingUp,
} from './utils/utils';
import type { ExtendedToolbarChildrenProps } from '../../types';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';

export const textSizeButtons: Record<TextSizeBlockType, string> = {
  unstyled: 'Normal Text',
  'header-two': 'Title',
  'header-three': 'Subtitle',
};

export default (props: ExtendedToolbarChildrenProps) => {
  const [showOptions, setShowOptions] = useState(false);

  const activeTextSizeRef = useRef<HTMLButtonElement>(null);
  const titleTextSizeRef = useRef<HTMLButtonElement>(null);
  const subtitleTextSizeRef = useRef<HTMLButtonElement>(null);
  const normalTextSizeRef = useRef<HTMLButtonElement>(null);

  const hideOptions = () => {
    setShowOptions(false);
  };

  const stylesContext = useContext(StylesContext);

  useClickOutside(
    [
      activeTextSizeRef,
      titleTextSizeRef,
      subtitleTextSizeRef,
      normalTextSizeRef,
    ],
    hideOptions,
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setShowOptions(false);
      activeTextSizeRef.current?.focus();
    }
  };

  const { setEditorState, getEditorState, rootId } = props;

  const textSizeRef: Record<
    TextSizeBlockType,
    React.RefObject<HTMLButtonElement>
  > = {
    unstyled: normalTextSizeRef,
    'header-two': titleTextSizeRef,
    'header-three': subtitleTextSizeRef,
  };

  const changeTextSize = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newTextSize: string,
  ) => {
    preventBubblingUp(e);

    const newState = getStateWithNewTextSize(newTextSize, getEditorState());
    hideOptions();
    setEditorState(newState);
  };

  const onClickHandler = (event: React.MouseEvent<any>) => {
    preventBubblingUp(event);
    setShowOptions(true);
  };

  const activeTextSize = getActiveTextSize(getEditorState());

  return (
    <div
      className={styles.buttonContainer}
      onMouseDown={preventBubblingUp}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        ref={activeTextSizeRef}
        className={classnames(styles.button, styles.buttonTextContent, {
          [styles.hovered]: stylesContext.hovered,
          [styles.focused]: stylesContext.focused,
          [styles.error]: stylesContext.error,
        })}
        onClick={onClickHandler}
        data-testid={TestIds.textSizeButton}
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={showOptions}
        aria-controls={`textSizeMenu_${rootId}`}
      >
        {textSizeButtons[activeTextSize]}
      </button>
      {showOptions && (
        <div
          role="menu"
          className={classnames(
            styles.menuContainer,
            {
              [styles.hovered]: stylesContext.hovered,
              [styles.focused]: stylesContext.focused,
              [styles.error]: stylesContext.error,
            },
            styles.inline,
          )}
          id={`textSizeMenu_${rootId}`}
        >
          {textSizeBlockTypes.map(textSize => (
            <button
              key={textSize}
              onClick={e => changeTextSize(e, textSize)}
              ref={textSizeRef[textSize]}
              className={classnames(
                styles.buttonTextContent,
                textSize === activeTextSize && styles.buttonTextSelected,
              )}
              data-testid={`${TestIds.textSizeButton}-${textSize}`}
              role="menuitemradio"
              aria-checked={activeTextSize === textSize}
            >
              {textSizeButtons[textSize]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
